import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, X, TrendingUp, Eye, Users, Clock, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

const BoostStatusModal = ({ isOpen, onClose, boostStatus, onBuyBoost }) => {
  if (!isOpen) return null;

  const { is_boosted, minutes_remaining, expires_at } = boostStatus;
  
  const formatTimeRemaining = (minutes) => {
    if (!minutes) return '0m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}t ${mins}m`;
    }
    return `${mins}m`;
  };

  const getProgressPercent = () => {
    if (!minutes_remaining) return 0;
    // Assume max 12 hours (720 minutes) for progress bar
    return Math.min(100, (minutes_remaining / 720) * 100);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="w-full max-w-sm bg-card rounded-3xl border border-border shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header with gradient */}
          <div className="relative bg-gradient-to-br from-accent via-orange-500 to-amber-500 p-6 text-center">
            <button 
              onClick={onClose}
              className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            
            {/* Animated lightning bolt */}
            <motion.div
              animate={{ 
                scale: [1, 1.1, 1],
                rotate: [0, 5, -5, 0]
              }}
              transition={{ 
                duration: 2, 
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="w-20 h-20 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm"
            >
              <Zap className="w-10 h-10 text-white fill-white" />
            </motion.div>
            
            <h2 className="text-2xl font-bold text-white mb-1">
              {is_boosted ? 'Boost Aktiv!' : 'Boost Din Profil'}
            </h2>
            <p className="text-white/80 text-sm">
              {is_boosted 
                ? 'Din profil vises først for alle' 
                : 'Bliv set af flere og få flere matches'}
            </p>
          </div>

          {/* Content */}
          <div className="p-6">
            {is_boosted ? (
              <>
                {/* Active Boost Stats */}
                <div className="bg-accent/10 rounded-2xl p-4 mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-accent" />
                      <span className="font-medium text-foreground">Tid tilbage</span>
                    </div>
                    <span className="text-2xl font-bold text-accent">
                      {formatTimeRemaining(minutes_remaining)}
                    </span>
                  </div>
                  
                  {/* Progress bar */}
                  <div className="h-3 bg-muted rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${getProgressPercent()}%` }}
                      transition={{ duration: 0.5 }}
                      className="h-full bg-gradient-to-r from-accent to-orange-500 rounded-full"
                    />
                  </div>
                </div>

                {/* Boost Effects */}
                <div className="space-y-3 mb-6">
                  <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    Boost Effekter
                  </h3>
                  
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl">
                    <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-green-500" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Prioriteret visning</p>
                      <p className="text-sm text-muted-foreground">Din profil vises først</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl">
                    <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                      <Eye className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Op til 10x flere visninger</p>
                      <p className="text-sm text-muted-foreground">Markant øget synlighed</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl">
                    <div className="w-10 h-10 rounded-full bg-pink-500/10 flex items-center justify-center">
                      <Users className="w-5 h-5 text-pink-500" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Flere matches</p>
                      <p className="text-sm text-muted-foreground">Større chance for match</p>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={onClose}
                  className="w-full bg-gradient-to-r from-accent to-orange-500 hover:from-accent/90 hover:to-orange-500/90 text-white rounded-full h-12 text-lg font-semibold"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Fantastisk!
                </Button>
              </>
            ) : (
              <>
                {/* Benefits when not boosted */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl">
                    <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Bliv set først</p>
                      <p className="text-sm text-muted-foreground">Din profil vises øverst</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl">
                    <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                      <Eye className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">10x flere visninger</p>
                      <p className="text-sm text-muted-foreground">I boost-perioden</p>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={() => {
                    onClose();
                    onBuyBoost();
                  }}
                  className="w-full bg-gradient-to-r from-accent to-orange-500 hover:from-accent/90 hover:to-orange-500/90 text-white rounded-full h-12 text-lg font-semibold"
                >
                  <Zap className="w-5 h-5 mr-2" />
                  Køb Boost
                </Button>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BoostStatusModal;
